import {cloneDeep, get, has, head, isEmpty, keyBy, orderBy} from "lodash"
import {v4 as uuid} from "uuid"
import {DataType, DefaultDataType, DigitalDocumentFieldSetUp, SelectStaffInstructorType, TaskType} from "types/tasks"
import moment from "moment"

export const useCalculateFields = () => {
    const margin = 10

    const getScaleInfo = ({taskData, pageRef}) => {
        const pageHeight = pageRef.current?.offsetHeight
        const pageWidth = pageRef.current?.offsetWidth
        const {width = pageWidth, height = pageHeight} = taskData.digitalDocument.pages?.[0] || {}

        const scaleHeight = height / pageHeight
        const scaleWidth = width / pageWidth

        return {scaleHeight, scaleWidth, pageHeight}
    }

    const scalePlaceInformation = (placeInformation, {scaleHeight, scaleWidth}, isSubmit = true) => {
        const {top, left, width, height} = placeInformation

        if (isSubmit) {
            return {
                width: width * scaleWidth,
                height: height * scaleHeight,
                top: top * scaleHeight,
                left: left * scaleWidth
            }
        }

        return {
            width: width / scaleWidth,
            height: height / scaleHeight,
            top: top / scaleHeight,
            left: left / scaleWidth
        }
    }

    const initSignature = (
        field,
        {placeInformation, signatureType, newFieldValues, scaleHeight, scaleWidth, parentId, extraInfo, pageHeight}
    ) => {
        if (placeInformation && placeInformation["dateField"]) {
            const dateFieldPlaceInfo = scalePlaceInformation(
                placeInformation["dateField"],
                {scaleHeight, scaleWidth},
                false
            )
            dateFieldPlaceInfo.top = dateFieldPlaceInfo.top + (field.page - 1) * (pageHeight + margin)
            newFieldValues.push({
                id: uuid(),
                parentId,
                type: DigitalDocumentFieldSetUp.DATE_FIELD,
                signatureType,
                placeInformation: dateFieldPlaceInfo,
                originalPlaceInformation: cloneDeep(placeInformation["dateField"]),
                ...extraInfo
            })
        }
    }

    const calculateFields = ({
        usersData,
        taskUsers,
        taskUserId,
        taskData,
        pageRef,
        signerId,
        isLeadInstructor,
        isInstructor,
        subTaskAnswers,
        canSign,
        setRemarksData
    }) => {
        let oldAnswer = null
        const isCustomDigitalTask = taskData.type === TaskType.CUSTOM_DD
        const usersDataKeyByParticipantId = keyBy(usersData, "participantId")
        const newFieldValues = []
        const {scaleHeight, scaleWidth, pageHeight} = getScaleInfo({taskData, pageRef})
        const taskUsersKeyBySignerId = keyBy(taskUsers, "signerId")
        const taskUser = taskUsers.find((item) => item.id === taskUserId)
        const remarksByFieldId = keyBy(taskUser?.remarks, "field")

        if (taskUser) {
            oldAnswer = taskUser.answers
            setRemarksData({remarkedReason: taskUser?.remarkedReason, remarks: taskUser.remarks})
        }

        const newFields = taskData.digitalDocument.fields
            .map((taskField) => {
                const field = cloneDeep(taskField)
                const placeInformation = field.placeInformation[0]
                const fieldValues = field.fieldValues
                field.required = !!field.required
                field.originalPlaceInformation = cloneDeep(placeInformation)
                const {signatureType} = field
                let isYourField = (field.signerIds || []).includes(signerId)
                if (isCustomDigitalTask && !isYourField) {
                    const signerTypes = taskData.digitalDocument.signers
                        .filter((item) => taskField.signerIds.includes(item.id))
                        .map((item) => item.jsonData.type)
                    isYourField = signerTypes.some((item) => {
                        if (item === SelectStaffInstructorType.LeadInstructor) {
                            return isLeadInstructor
                        }
                        if (item === SelectStaffInstructorType.Instructor) {
                            return isInstructor
                        }
                        return false
                    })
                }
                const firstSignerId: any = head(field.signerIds)
                const backgroundColor = get(taskUsersKeyBySignerId, [firstSignerId, "backgroundColor"], "")
                if (placeInformation) {
                    const {height, left, top, width} = placeInformation
                    field.placeInformation = scalePlaceInformation(
                        {height, left, top, width},
                        {scaleHeight, scaleWidth},
                        false
                    )
                    field.placeInformation.top = field.placeInformation.top + (field.page - 1) * (pageHeight + margin)
                }

                switch (field.type) {
                    case DigitalDocumentFieldSetUp.SIGNATURE: {
                        let extraInfo: any = {isYourField}

                        if (!canSign || !isYourField) {
                            const answer = get(subTaskAnswers, [field.id], "")
                            const hasSigned = answer === "signed"
                            field.isSigned = !!answer
                            field.value = hasSigned
                                ? get(taskUsersKeyBySignerId, [firstSignerId, "signature"], "")
                                : null
                            field.backgroundColor = backgroundColor
                            extraInfo = {
                                isSigned: true,
                                backgroundColor,
                                date: hasSigned ? get(taskUsersKeyBySignerId, [firstSignerId, "submittedAt"], "") : null
                            }
                        } else if (oldAnswer) {
                            field.isSigned = true
                            field.value = get(oldAnswer, [field.id]) === "signed" ? taskUser.signature : null
                        }
                        initSignature(field, {
                            placeInformation,
                            signatureType,
                            newFieldValues,
                            scaleHeight,
                            scaleWidth,
                            parentId: field.id,
                            extraInfo,
                            pageHeight
                        })
                        break
                    }
                    case DigitalDocumentFieldSetUp.INITIAL: {
                        if (fieldValues) {
                            let answer = get(subTaskAnswers, [field.id], "")
                            answer = (answer || "").split(",")
                            const oldInitialAnswer =
                                oldAnswer && oldAnswer[field.id] ? oldAnswer[field.id].split(",") : null
                            const initialFieldValues = fieldValues.map((fieldValue, index) => {
                                const {height, left, top, width} = fieldValue.position
                                const fieldValuePlaceInformation = scalePlaceInformation(
                                    {height, left, top, width},
                                    {scaleHeight, scaleWidth},
                                    false
                                )
                                const childFieldPage = fieldValue.page || field.page
                                fieldValuePlaceInformation.top =
                                    fieldValuePlaceInformation.top + (childFieldPage - 1) * (pageHeight + margin)
                                if (!canSign || !isYourField) {
                                    const hasSigned = answer[index] === "signed"
                                    return {
                                        id: fieldValue.id,
                                        uuid: fieldValue.id,
                                        parentUuid: field.uuid,
                                        parsedDependencies: field.parsedDependencies,
                                        type: DigitalDocumentFieldSetUp.INITIAL,
                                        parentId: field.id,
                                        signatureType,
                                        initialType: field.initialType,
                                        isSigned: true,
                                        value: hasSigned
                                            ? get(taskUsersKeyBySignerId, [firstSignerId, "initials"], "")
                                            : null,
                                        backgroundColor: backgroundColor,
                                        placeInformation: fieldValuePlaceInformation,
                                        isYourField
                                    }
                                }
                                const initialData: any = {
                                    id: fieldValue.id,
                                    uuid: fieldValue.id,
                                    parentUuid: field.uuid,
                                    parsedDependencies: field.parsedDependencies,
                                    type: DigitalDocumentFieldSetUp.INITIAL,
                                    parentId: field.id,
                                    signatureType,
                                    initialType: field.initialType,
                                    label: fieldValue.label,
                                    placeInformation: fieldValuePlaceInformation,
                                    originalPlaceInformation: cloneDeep(fieldValue.position),
                                    page: childFieldPage,
                                    required: !!field.required,
                                    isSigned: !isEmpty(answer),
                                    value: !isEmpty(answer) ? answer[index] === "signed" : undefined,
                                    isYourField
                                }
                                if (oldInitialAnswer) {
                                    initialData.isSigned = true
                                    initialData.value = answer[index] === "signed" ? taskUser.initials : null
                                    initialData.remarkComment =
                                        index === 0 ? get(remarksByFieldId, [field.id, "reason"]) : null
                                }
                                initialData.originalValue = initialData.value
                                return initialData
                            })
                            newFieldValues.splice(newFieldValues.length, 0, ...initialFieldValues)
                        }
                        break
                    }
                    case DigitalDocumentFieldSetUp.CHECKBOX: {
                        if (fieldValues) {
                            const oldCheckboxAnswer =
                                oldAnswer && oldAnswer[field.id] ? oldAnswer[field.id].split(",") : null
                            let answer = get(subTaskAnswers, [field.id], "")
                            answer = (answer || "").split(",")
                            const checkboxFieldValues = fieldValues.map((fieldValue, index) => {
                                const {height, left, top, width} = fieldValue.position
                                const fieldValuePlaceInformation = scalePlaceInformation(
                                    {height, left, top, width},
                                    {scaleHeight, scaleWidth},
                                    false
                                )
                                const childFieldPage = fieldValue.page || field.page
                                fieldValuePlaceInformation.top =
                                    fieldValuePlaceInformation.top + (childFieldPage - 1) * (pageHeight + margin)
                                if (!canSign || !isYourField) {
                                    const isChecked = answer[index] === "Yes"

                                    return {
                                        id: fieldValue.id,
                                        uuid: fieldValue.id,
                                        parentUuid: field.uuid,
                                        parsedDependencies: field.parsedDependencies,
                                        type: DigitalDocumentFieldSetUp.CHECKBOX,
                                        parentId: field.id,
                                        signatureType,
                                        checkboxType: field.checkboxType,
                                        isSigned: true,
                                        value: isChecked,
                                        backgroundColor: backgroundColor,
                                        placeInformation: fieldValuePlaceInformation,
                                        isYourField
                                    }
                                }
                                const checkboxData: any = {
                                    id: fieldValue.id,
                                    uuid: fieldValue.id,
                                    parentUuid: field.uuid,
                                    parsedDependencies: field.parsedDependencies,
                                    type: DigitalDocumentFieldSetUp.CHECKBOX,
                                    parentId: field.id,
                                    signatureType,
                                    checkboxType: field.checkboxType,
                                    label: fieldValue.label,
                                    placeInformation: fieldValuePlaceInformation,
                                    originalPlaceInformation: cloneDeep(fieldValue.position),
                                    page: childFieldPage,
                                    required: !!field.required,
                                    isSigned: !isEmpty(answer),
                                    value: !isEmpty(answer) ? answer[index] === "Yes" : undefined,
                                    isYourField
                                }
                                if (oldCheckboxAnswer) {
                                    checkboxData.isSigned = true
                                    checkboxData.value = oldCheckboxAnswer[index] === "Yes"
                                    checkboxData.remarkComment =
                                        index === 0 ? get(remarksByFieldId, [field.id, "reason"]) : null
                                }
                                checkboxData.originalValue = checkboxData.value
                                return checkboxData
                            })
                            newFieldValues.splice(newFieldValues.length, 0, ...checkboxFieldValues)
                        }
                        break
                    }
                    case DigitalDocumentFieldSetUp.SELECT: {
                        const answer = get(subTaskAnswers, [field.id], "")
                        if (!canSign || !isYourField) {
                            field.readOnly = true
                            field.isSigned = true
                            field.value = answer
                            field.backgroundColor = backgroundColor
                        } else if (oldAnswer || answer) {
                            const value = oldAnswer ? get(oldAnswer, [field.id]) : answer
                            const valueOption = field.fieldValues.find((item) => item.option === value)
                            field.isSigned = true
                            field.value = valueOption
                        }
                        break
                    }
                    case DigitalDocumentFieldSetUp.TEXT: {
                        const isAutocompletedField =
                            field.defaultDataType === DefaultDataType.STUDENT_DATA && field.defaultData
                        field.isAutocompletedField = isAutocompletedField
                        const answer = get(subTaskAnswers, [field.id], "")
                        if (!canSign || !isYourField) {
                            field.readOnly = true
                            field.isSigned = true
                            field.value = answer
                            field.backgroundColor = backgroundColor
                        } else if (oldAnswer) {
                            field.isSigned = true
                            field.value = get(oldAnswer, [field.id])
                        } else if (field.defaultDataType === DefaultDataType.STUDENT_DATA) {
                            field.isSigned = true
                            const defaultUserValue = get(
                                usersDataKeyByParticipantId,
                                [field.defaultParticipant, field.defaultData],
                                ""
                            )
                            const dateFormat = get(
                                usersDataKeyByParticipantId,
                                [field.defaultParticipant, "dateFormat"],
                                "MM/DD/YYYY"
                            )
                            const defaultValue =
                                field.dataType === DataType.DATE
                                    ? moment(defaultUserValue, dateFormat)
                                    : defaultUserValue
                            const hasAnswer = has(subTaskAnswers, field.id)
                            field.value = hasAnswer ? answer : defaultValue
                        } else if (answer) {
                            field.isSigned = true
                            field.value = answer
                        }
                        break
                    }
                    case DigitalDocumentFieldSetUp.GRADE: {
                        const answer = get(subTaskAnswers, [field.id], "")

                        if (!canSign || !isYourField) {
                            field.readOnly = true
                            field.isSigned = true
                            field.value = answer
                            field.backgroundColor = backgroundColor
                        } else if (oldAnswer) {
                            field.isSigned = true
                            field.value = get(oldAnswer, [field.id])
                        } else if (answer) {
                            field.isSigned = true
                            field.value = answer
                        }
                        break
                    }
                    case DigitalDocumentFieldSetUp.FILE: {
                        const answer = get(subTaskAnswers, [field.id], "")

                        if (!canSign || !isYourField) {
                            field.readOnly = true
                            field.isSigned = true
                            field.value = answer
                            field.backgroundColor = backgroundColor
                        } else if (oldAnswer) {
                            field.isSigned = true
                            field.value = get(oldAnswer, [field.id])
                        } else if (answer) {
                            field.isSigned = true
                            field.value = answer
                        }
                        break
                    }
                    default:
                        break
                }
                field.isYourField = isYourField
                field.remarkComment = get(remarksByFieldId, [field.id, "reason"])
                field.originalValue = field.value
                return field
            })
            .filter((field) => {
                if (field.isAutocompletedField) {
                    return true
                }
                if (!field.isYourField) {
                    return field.value
                }
                return (
                    field.type !== DigitalDocumentFieldSetUp.INITIAL &&
                    field.type !== DigitalDocumentFieldSetUp.CHECKBOX
                )
            })

        const allFields = [...newFields, ...newFieldValues]
        const textFields = allFields.filter((field) =>
            [DigitalDocumentFieldSetUp.TEXT, DigitalDocumentFieldSetUp.GRADE].includes(field.type)
        )
        const restFields = allFields.filter(
            (field) => ![DigitalDocumentFieldSetUp.TEXT, DigitalDocumentFieldSetUp.GRADE].includes(field.type)
        )
        const sortedTextFields = orderBy(textFields, ["placeInformation.top", "placeInformation.left"])

        return [...restFields, ...sortedTextFields]
    }

    return {calculateFields, scalePlaceInformation, getScaleInfo, margin}
}
